<script>
import Layout from '@layouts/main'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'

import estoqueService from '@/src/services/api-receituario/estoque'
import produtoService from '@/src/services/produto_estoque'
import {
  buildStockSummary,
  formatMovementDate,
  summarizeStock,
} from './stock-helpers'

export default {
  components: { Layout, Loading },
  data() {
    return {
      isLoading: false,
      produtos: [],
      movimentacoes: [],
      resumoProdutos: [],
      indicadores: {
        totalProdutos: 0,
        abaixoMinimo: 0,
        semSaldo: 0,
        acimaMaximo: 0,
        semParametros: 0,
        totalMovimentacoes: 0,
      },
      cards: [
        {
          key: 'totalProdutos',
          label: 'Produtos monitorados',
          tone: 'neutral',
          helper: 'Base vinda do cadastro de produtos atual.',
        },
        {
          key: 'abaixoMinimo',
          label: 'Abaixo do minimo',
          tone: 'warning',
          helper: 'Precisam de reposicao com mais urgencia.',
        },
        {
          key: 'semSaldo',
          label: 'Sem saldo',
          tone: 'danger',
          helper: 'Produtos zerados na ultima posicao encontrada.',
        },
        {
          key: 'semParametros',
          label: 'Sem parametrizacao',
          tone: 'info',
          helper: 'Produtos sem minimo ou maximo configurados.',
        },
      ],
      resumoFields: [
        { key: 'nome', label: 'Produto' },
        { key: 'disponivel', label: 'Atual', class: 'text-right' },
        { key: 'minimo', label: 'Minimo', class: 'text-right' },
        { key: 'maximo', label: 'Maximo', class: 'text-right' },
        { key: 'suggestedQuantity', label: 'Sugestao', class: 'text-right' },
        { key: 'statusLabel', label: 'Status' },
        { key: 'ultima_movimentacao', label: 'Ultima movimentacao' },
      ],
      movimentacaoFields: [
        { key: 'nome', label: 'Produto' },
        { key: 'cliente', label: 'Cliente' },
        { key: 'tipo_movimento', label: 'Tipo' },
        { key: 'disponivel', label: 'Saldo', class: 'text-right' },
        { key: 'data', label: 'Data' },
      ],
    }
  },
  mounted() {
    this.carregarDashboard()
  },
  computed: {
    produtosCriticos() {
      return this.resumoProdutos
        .filter((item) => item.status !== 'ok')
        .sort((a, b) => {
          const severity = {
            empty: 0,
            low: 1,
            high: 2,
            unconfigured: 3,
            ok: 4,
          }

          return severity[a.status] - severity[b.status]
        })
        .slice(0, 10)
    },
    ultimasMovimentacoes() {
      return [...this.movimentacoes]
        .sort((a, b) => {
          const timeA = new Date((a && a.data) || 0).getTime()
          const timeB = new Date((b && b.data) || 0).getTime()
          return timeB - timeA
        })
        .slice(0, 8)
    },
  },
  methods: {
    async carregarDashboard() {
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true

      try {
        const [movimentacaoResponse, produtoResponse] = await Promise.all([
          estoqueService.listar(token),
          produtoService.listar(token),
        ])

        this.movimentacoes = movimentacaoResponse.data.estoques || []
        this.produtos = produtoResponse.data.produtos || []
        this.resumoProdutos = buildStockSummary(
          this.movimentacoes,
          this.produtos
        )
        this.indicadores = summarizeStock(
          this.resumoProdutos,
          this.movimentacoes
        )
      } catch (error) {
        this.$bvToast.toast(
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
            'Nao foi possivel carregar o estoque.',
          {
            variant: 'danger',
            solid: false,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
          }
        )
      } finally {
        this.isLoading = false
      }
    },
    formatDate(value) {
      return formatMovementDate(value)
    },
    statusClass(status) {
      return `stock-status-badge is-${status}`
    },
    toneClass(tone) {
      return `stock-kpi-card is-${tone}`
    },
    goTo(path) {
      this.$router.push(path)
    },
  },
}
</script>

<template>
  <Layout>
    <div class="stock-page-shell">
      <div class="stock-page-hero">
        <div>
          <span class="stock-page-eyebrow">Gestao de Estoque</span>
          <h1 class="stock-page-title">Dashboard Estoque</h1>
          <p class="stock-page-subtitle">
            Uma visao rapida do saldo, dos itens criticos e dos proximos passos
            do seu estoque.
          </p>
        </div>
        <div class="stock-page-actions">
          <button class="btn btn-outline-primary" @click="goTo('/cadastros/produto')">
            Abrir cadastro de produto
          </button>
          <button class="btn btn-webreceita" @click="goTo('/estoque/sugestao-reposicao')">
            Ver sugestoes
          </button>
        </div>
      </div>

      <div class="stock-kpi-grid">
        <article
          v-for="card in cards"
          :key="card.key"
          :class="toneClass(card.tone)"
        >
          <span class="stock-kpi-label">{{ card.label }}</span>
          <strong class="stock-kpi-value">{{ indicadores[card.key] }}</strong>
          <span class="stock-kpi-helper">{{ card.helper }}</span>
        </article>
      </div>

      <div class="stock-panel-grid">
        <section class="stock-panel">
          <div class="stock-panel-head">
            <div>
              <h2>Produtos que pedem atencao</h2>
              <p>Itens com saldo zerado, abaixo do minimo ou sem configuracao.</p>
            </div>
            <button class="btn btn-light" @click="goTo('/estoque/ajuste-estoque')">
              Ajustar estoque
            </button>
          </div>

          <b-table
            small
            responsive
            hover
            :items="produtosCriticos"
            :fields="resumoFields"
            show-empty
            class="stock-table"
          >
            <template #empty>
              <div class="stock-empty-state">
                Nenhum produto critico encontrado no momento.
              </div>
            </template>
            <template #cell(statusLabel)="row">
              <span :class="statusClass(row.item.status)">
                {{ row.item.statusLabel }}
              </span>
            </template>
            <template #cell(ultima_movimentacao)="row">
              {{ formatDate(row.item.ultima_movimentacao) }}
            </template>
          </b-table>
        </section>

        <section class="stock-panel">
          <div class="stock-panel-head">
            <div>
              <h2>Ultimas movimentacoes</h2>
              <p>Historico recente para acompanhar entradas e saidas.</p>
            </div>
            <button class="btn btn-light" @click="goTo('/estoque/movimentacao')">
              Abrir movimentacoes
            </button>
          </div>

          <b-table
            small
            responsive
            hover
            :items="ultimasMovimentacoes"
            :fields="movimentacaoFields"
            show-empty
            class="stock-table"
          >
            <template #empty>
              <div class="stock-empty-state">
                Nenhuma movimentacao cadastrada ainda.
              </div>
            </template>
            <template #cell(tipo_movimento)="row">
              <span :class="statusClass(row.item.tipo_movimento === 'E' ? 'ok' : 'info')">
                {{ row.item.tipo_movimento === 'E' ? 'Entrada' : 'Saida' }}
              </span>
            </template>
            <template #cell(data)="row">
              {{ formatDate(row.item.data) }}
            </template>
          </b-table>
        </section>
      </div>

      <b-overlay :show="isLoading" no-wrap />
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
.stock-page-shell {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.stock-page-hero,
.stock-panel,
.stock-kpi-card {
  background: #fff;
  border: 1px solid rgba(0, 142, 117, 0.14);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(15, 61, 52, 0.06);
}

.stock-page-hero {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 28px 30px;
}

.stock-page-eyebrow {
  display: inline-block;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #008e75;
}

.stock-page-title {
  margin: 0 0 8px;
  font-size: 34px;
  font-weight: 700;
  color: #11493f;
}

.stock-page-subtitle {
  max-width: 700px;
  margin: 0;
  font-size: 16px;
  color: #53706a;
}

.stock-page-actions {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.stock-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.stock-kpi-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 22px;
}

.stock-kpi-card.is-warning {
  background: linear-gradient(180deg, #fff9ee 0%, #ffffff 100%);
}

.stock-kpi-card.is-danger {
  background: linear-gradient(180deg, #fff4f2 0%, #ffffff 100%);
}

.stock-kpi-card.is-info {
  background: linear-gradient(180deg, #f2fbf8 0%, #ffffff 100%);
}

.stock-kpi-label {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #53706a;
}

.stock-kpi-value {
  font-size: 42px;
  line-height: 1;
  color: #0d6555;
}

.stock-kpi-helper {
  font-size: 13px;
  color: #6f8a84;
}

.stock-panel-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: 20px;
}

.stock-panel {
  padding: 24px;
}

.stock-panel-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
}

.stock-panel-head h2 {
  margin: 0 0 6px;
  font-size: 21px;
  color: #11493f;
}

.stock-panel-head p {
  margin: 0;
  color: #69847f;
}

.stock-table::v-deep table {
  margin-bottom: 0;
}

.stock-empty-state {
  padding: 22px 12px;
  text-align: center;
  color: #6f8a84;
}

.stock-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 130px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.stock-status-badge.is-empty,
.stock-status-badge.is-danger {
  background: rgba(214, 58, 58, 0.12);
  color: #bf3030;
}

.stock-status-badge.is-low,
.stock-status-badge.is-warning {
  background: rgba(240, 145, 31, 0.14);
  color: #b86e12;
}

.stock-status-badge.is-high {
  background: rgba(43, 120, 228, 0.12);
  color: #2d6fd0;
}

.stock-status-badge.is-unconfigured,
.stock-status-badge.is-info {
  background: rgba(0, 142, 117, 0.1);
  color: #0d6555;
}

.stock-status-badge.is-ok {
  background: rgba(0, 142, 117, 0.1);
  color: #0d6555;
}

@media (max-width: 1199px) {
  .stock-kpi-grid,
  .stock-panel-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 767px) {
  .stock-page-hero,
  .stock-panel-head {
    flex-direction: column;
  }

  .stock-kpi-grid,
  .stock-panel-grid {
    grid-template-columns: 1fr;
  }

  .stock-page-title {
    font-size: 28px;
  }
}
</style>
